import { useMemo, useState } from "react";
import { Repeat, FileDown, CheckCircle2, AlertTriangle, ExternalLink } from "lucide-react";
import { jsPDF } from "jspdf";
import { toast } from "sonner";

// ============================================================================
// Backdoor Roth Matrix — Premium Tax Shield step-by-step guide for high-earners
// blocked from direct Roth IRA contributions by IRS § 408A(c)(3) phase-outs.
// Purely educational; no wire transfers here. Renders an income check and a
// 6-step CPA-executed playbook; export button drops a print-ready PDF.
// ============================================================================
const ROTH_PHASEOUT_2026 = {
  single_start:  150000, // Below this = direct Roth allowed (2026 projection)
  single_end:    165000, // Above this = fully phased out
  mfj_start:     236000,
  mfj_end:       246000,
};

const STEPS = [
  { title: "Open a Traditional IRA", body: "Establish a traditional IRA at your custodian if one does not exist. No income limit applies to contribution eligibility — only to deductibility." },
  { title: "Make a Non-Deductible Contribution", body: "Contribute up to the annual IRA limit as a non-deductible contribution. This creates basis in the IRA." },
  { title: "File IRS Form 8606", body: "Your CPA files Form 8606 for the contribution year to track basis. Skipping this is the #1 cause of double-taxation on conversion." },
  { title: "Wait a Short Interval", body: "Best practice: wait a business day or two between the contribution and the conversion (there is no statutory required waiting period, but a small gap reduces step-transaction scrutiny)." },
  { title: "Convert to Roth IRA", body: "Direct-transfer the traditional IRA balance to a Roth IRA. The taxable portion of the conversion equals earnings + any pre-tax dollars still in ANY traditional/SEP/SIMPLE IRA (pro-rata rule — IRC § 408(d)(2))." },
  { title: "Reconcile at Tax Time", body: "Your CPA reports the conversion on Form 8606 Part II. Pay tax on the earnings-slice only; the non-deductible basis flows through tax-free per Notice 2014-54." },
];

const fmt = (n) => "$" + Number(Math.round(n)).toLocaleString();

export function BackdoorRothMatrix({ defaultIncome = 185000 }) {
  const [income, setIncome] = useState(defaultIncome);
  const [filing, setFiling] = useState("single"); // 'single' | 'mfj'
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const bounds = filing === "mfj"
    ? { start: ROTH_PHASEOUT_2026.mfj_start, end: ROTH_PHASEOUT_2026.mfj_end }
    : { start: ROTH_PHASEOUT_2026.single_start, end: ROTH_PHASEOUT_2026.single_end };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const status = useMemo(() => {
    if (income < bounds.start) return { code: "direct", label: "Below phase-out · Direct Roth allowed", color: "#10b981" };
    if (income > bounds.end)   return { code: "blocked", label: "Above phase-out · Direct Roth blocked · Backdoor recommended", color: "#f59e0b" };
    return { code: "partial", label: "Inside phase-out · Partial direct Roth · Backdoor safer", color: "#facc15" };
  }, [income, bounds]);

  const exportCPAGuide = () => {
    try {
      const doc = new jsPDF({ unit: "pt", format: "letter" });
      const W = doc.internal.pageSize.getWidth();
      const M = 48;
      const now = new Date();
      doc.setDrawColor(20); doc.setLineWidth(1.2);
      doc.line(M, 60, W - M, 60);
      doc.setFont("helvetica", "bold"); doc.setFontSize(18);
      doc.text("Backdoor Roth IRA · CPA Playbook", M, 84);
      doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(90);
      doc.text(`Prepared ${now.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })} · Receipt Sheets`, M, 100);
      doc.setTextColor(0);

      let y = 140;
      doc.setFont("helvetica", "bold"); doc.setFontSize(11);
      doc.text("Filer Snapshot", M, y); y += 6;
      doc.setDrawColor(200); doc.line(M, y, W - M, y); y += 18;
      doc.setFont("helvetica", "normal"); doc.setFontSize(11);
      doc.text(`Filing status:`, M, y); doc.text(filing === "mfj" ? "Married filing jointly" : "Single", W - M, y, { align: "right" }); y += 16;
      doc.text(`Modified AGI (est.):`, M, y); doc.text(fmt(income), W - M, y, { align: "right" }); y += 16;
      doc.text(`Roth phase-out (2026):`, M, y); doc.text(`${fmt(bounds.start)} – ${fmt(bounds.end)}`, W - M, y, { align: "right" }); y += 16;
      doc.text(`Determination:`, M, y); doc.text(status.label, W - M, y, { align: "right" }); y += 24;

      doc.setFont("helvetica", "bold"); doc.setFontSize(11);
      doc.text("CPA Execution Playbook", M, y); y += 6;
      doc.setDrawColor(200); doc.line(M, y, W - M, y); y += 18;
      doc.setFont("helvetica", "normal"); doc.setFontSize(11);
      STEPS.forEach((s, i) => {
        doc.setFont("helvetica", "bold"); doc.text(`${i + 1}. ${s.title}`, M, y); y += 14;
        doc.setFont("helvetica", "normal");
        const lines = doc.splitTextToSize(s.body, W - M * 2);
        doc.text(lines, M, y); y += lines.length * 13 + 8;
      });

      y = Math.max(y, 660);
      doc.setDrawColor(160); doc.line(M, y, W - M, y);
      doc.setFont("helvetica", "italic"); doc.setFontSize(8); doc.setTextColor(110);
      doc.text("Statutory basis: IRC § 408A(c)(3), § 408(d)(2), Notice 2014-54, Form 8606 (Parts I & II).", M, y + 14);
      doc.text("Educational tool only — not legal or tax advice. Confirm with a qualified CPA before executing.", M, y + 26);

      const fname = `backdoor_roth_playbook_${now.toISOString().slice(0,10)}.pdf`;
      doc.save(fname);
      toast.success("CPA guide exported", { description: fname });
    } catch {
      toast.error("Export failed — try again");
    }
  };

  return (
    <div
      data-testid="backdoor-roth-matrix"
      className="glass rounded-2xl p-6 relative overflow-hidden"
      style={{ borderColor: "rgba(253,224,71,0.4)" }}
    >
      <div
        aria-hidden="true"
        className="absolute -top-24 -left-16 w-72 h-72 rounded-full pointer-events-none"
        style={{
          background: "radial-gradient(circle, rgba(253,224,71,0.25), transparent 65%)",
          filter: "blur(20px)",
        }}
      />
      <div className="relative">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-yellow-300 flex items-center gap-1.5">
            <Repeat size={12} /> Backdoor Roth Matrix · IRC § 408A(c)(3)
          </div>
          <button
            type="button"
            onClick={exportCPAGuide}
            data-testid="backdoor-roth-export-btn"
            className="text-[10px] font-mono uppercase tracking-widest px-2.5 py-1 rounded-md border border-yellow-500/40 bg-yellow-500/10 text-yellow-100 hover:bg-yellow-500/20 hover:-translate-y-px transition-all flex items-center gap-1.5"
            title="Export a print-ready CPA execution playbook"
          >
            <FileDown size={11} /> Export CPA Guide
          </button>
        </div>

        {/* Filer inputs */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          <div>
            <label className="text-[10px] font-mono uppercase tracking-[0.24em] text-yellow-300 mb-1 block">
              Filing status
            </label>
            <div className="inline-flex rounded-md border border-yellow-500/40 overflow-hidden">
              {["single", "mfj"].map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setFiling(k)}
                  data-testid={`backdoor-roth-filing-${k}`}
                  className={`px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest transition-colors ${filing === k ? "bg-yellow-500/25 text-yellow-100" : "text-yellow-300/70 hover:text-white hover:bg-yellow-500/10"}`}
                >
                  {k === "mfj" ? "Married · Joint" : "Single"}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase tracking-[0.24em] text-yellow-300 mb-1 block">
              Modified AGI (annual)
            </label>
            <input
              type="number"
              min="0"
              step="1000"
              value={income}
              onChange={(e) => setIncome(Math.max(0, Number(e.target.value || 0)))}
              data-testid="backdoor-roth-income-input"
              className="w-full rounded-lg bg-slate-900/70 border border-yellow-500/25 focus:border-yellow-400 outline-none text-white text-sm p-2 font-mono"
            />
          </div>
        </div>

        {/* Determination */}
        <div
          data-testid="backdoor-roth-status"
          data-status={status.code}
          className="rounded-lg p-3 mb-4 border flex items-start gap-2"
          style={{ borderColor: status.color + "80", background: status.color + "12", color: status.color }}
        >
          {status.code === "direct"
            ? <CheckCircle2 size={16} />
            : <AlertTriangle size={16} />}
          <div className="text-sm font-mono">
            {status.label}
            <div className="text-[10px] uppercase tracking-widest opacity-80 mt-1">
              Phase-out band ({filing === "mfj" ? "MFJ" : "single"}): {fmt(bounds.start)} – {fmt(bounds.end)}
            </div>
          </div>
        </div>

        {/* Steps */}
        <ol className="space-y-3" data-testid="backdoor-roth-steps">
          {STEPS.map((s, i) => (
            <li
              key={s.title}
              data-testid={`backdoor-roth-step-${i + 1}`}
              className="flex items-start gap-3 rounded-lg p-3 border border-yellow-500/15 bg-slate-950/40"
            >
              <span className="shrink-0 mt-0.5 w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold font-mono bg-yellow-500/20 text-yellow-100 border border-yellow-500/40">
                {i + 1}
              </span>
              <div className="min-w-0">
                <div className="text-white font-semibold text-sm">{s.title}</div>
                <div className="text-slate-300 text-sm mt-0.5 leading-relaxed">{s.body}</div>
              </div>
            </li>
          ))}
        </ol>

        <a
          href="https://www.law.cornell.edu/uscode/text/26/408A"
          target="_blank"
          rel="noopener noreferrer"
          className="mt-4 inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest text-yellow-200 hover:text-yellow-100"
        >
          Full statute · IRC § 408A on Cornell LII <ExternalLink size={10} />
        </a>
        <div className="mt-4 pt-3 border-t border-white/5 text-[10px] font-mono uppercase tracking-widest text-slate-500">
          Educational tool · not legal or tax advice · confirm with a qualified CPA
        </div>
      </div>
    </div>
  );
}

export default BackdoorRothMatrix;
